import React from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {ROUTES} from 'app/router/routes';
import {ArrowRightIcon, DocumentsIcon, PlusIcon, StatusesIcon} from 'shared/icons';
import {usePaymentWaysPreview, useProfile, useProfileLinksUnlocked} from '../../model';
import {ProfileAlertsBanner} from '../ProfileAlertsBanner';
import type {ProfilePaymentMethod} from '../../model/paymentMethod';
import type {PaymentWayPreview} from '../../model/paymentWaysPreview';
import {AddPaymentMethodDrawer} from '../AddPaymentMethodDrawer';
import {PaymentWayDetailsDrawer, type PaymentWaySelection} from '../PaymentWayDetailsDrawer';
import styles from './ProfileHub.module.css';

function AutoReplyIcon(): React.ReactElement {
	return (
		<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path d="M26 11V30H6V2H17.1113L26 11ZM12 18.5H15.5V23L20 16.5H16.5V12L12 18.5Z" fill="black" />
		</svg>
	);
}

function PrimaryCheckIcon(): React.ReactElement {
	return (
		<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path d="M3 8.915L8.05412 14L17 5" stroke="#F3F3F3" stroke-width="1.6" />
		</svg>
	);
}

function BankFallbackIcon(): React.ReactElement {
	return (
		<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
			<path d="M3 10L12 4L21 10" stroke="currentColor" strokeWidth="1.6" />
			<path d="M5 10V19H19V10" stroke="currentColor" strokeWidth="1.6" />
			<path d="M3 19H21" stroke="currentColor" strokeWidth="1.6" />
		</svg>
	);
}

const NAV_CARDS = [
	{key: 'documents', label: 'Документы', icon: <DocumentsIcon />},
	{key: 'statuses', label: 'Статусы', icon: <StatusesIcon />},
	{key: 'auto-reply', label: 'Автоотклик', icon: <AutoReplyIcon />},
] as const;

const LINK_ITEMS = [
	{key: 'receipt', label: 'Сформировать чек'},
	{key: 'certificate', label: 'Сформировать справку'},
	{key: 'terms', label: 'Условия и правила'},
] as const;

export function ProfileHub(): React.ReactElement {
	const navigate = useNavigate();
	const [showAllPayments, setShowAllPayments] = React.useState(false);
	const [isAddPaymentOpen, setIsAddPaymentOpen] = React.useState(false);
	const [selectedPayment, setSelectedPayment] = React.useState<PaymentWaySelection | null>(null);
	const {profile, isLoading: isProfileLoading, isIdentityVerificationPending} = useProfile();
	const linksUnlocked = useProfileLinksUnlocked();
	const showDataLink = linksUnlocked || isIdentityVerificationPending;
	const {
		items,
		totalCount,
		hasMore,
		isLoading: isPaymentsLoading,
	} = usePaymentWaysPreview({
		expanded: showAllPayments,
	});
	const displayName = profile.firstName !== '—' ? profile.firstName : 'Профиль';
	const linkItems = linksUnlocked ? LINK_ITEMS : LINK_ITEMS.filter((item) => item.key === 'terms');

	const handleOpenPayment = (item: PaymentWayPreview) => {
		setSelectedPayment({type: item.type, id: item.wayId});
	};

	const handleSelectPaymentMethod = (method: ProfilePaymentMethod) => {
		if (method === 'executor_account') {
			navigate(ROUTES.profileAddAccountPayment);
			return;
		}

		if (method === 'bank_card') {
			navigate(ROUTES.profileAddCardPayment);
			return;
		}

		if (method === 'sbp_account') {
			navigate(ROUTES.profileAddSbpPayment);
		}
	};

	return (
		<div className={styles.root}>
			<header className={styles.header}>
				<div className={styles.headerInner}>
					<h1 className={styles.name}>{isProfileLoading ? '…' : displayName}</h1>
					{showDataLink ? (
						<Link to={ROUTES.profileData} className={styles.dataLink}>
							Данные
							<span className={styles.dataLinkIcon} aria-hidden />
						</Link>
					) : null}
				</div>
			</header>

			<div className={styles.content}>
				<div className={styles.contentInner}>
					<ProfileAlertsBanner className="mb-0" />
					{linksUnlocked ? (
						<section className={styles.section} aria-labelledby="profile-payments-title">
							<div className={styles.sectionHeader}>
								<h2 id="profile-payments-title" className={styles.sectionTitle}>
									Карты и счета
									{totalCount > 0 ? <span className={styles.sectionCount}>{totalCount}</span> : null}
								</h2>
								<button
									type="button"
									className={styles.addButton}
									aria-label="Добавить способ получения оплат"
									onClick={() => setIsAddPaymentOpen(true)}
								>
									<PlusIcon width={24} height={24} />
								</button>
							</div>

							{isPaymentsLoading ? <p className={styles.stateText}>Загрузка реквизитов...</p> : null}

							{!isPaymentsLoading && items.length === 0 ? (
								<p className={styles.emptyPayments}>Пока нет добавленных карт и счетов</p>
							) : null}

							{items.length > 0 ? (
								<ul className={styles.paymentList}>
									{items.map((item) => (
										<li key={item.id} className={styles.paymentCard}>
											<button
												type="button"
												className={styles.paymentCardButton}
												onClick={() => handleOpenPayment(item)}
											>
												<div className={styles.paymentIconWrap}>
													{item.iconUrl ? (
														<img
															className={
																item.isPrimary
																	? styles.paymentIcon
																	: `${styles.paymentIcon} ${styles.paymentIcon_padded}`
															}
															src={item.iconUrl}
															alt=""
														/>
													) : (
														<span
															className={
																item.isPrimary
																	? styles.paymentIconFallback
																	: `${styles.paymentIconFallback} ${styles.paymentIconFallback_padded}`
															}
														>
															<BankFallbackIcon />
														</span>
													)}
													{item.isPrimary ? (
														<span className={styles.primaryBadge} aria-label="Основной способ">
															<PrimaryCheckIcon />
														</span>
													) : null}
												</div>
												<div className={styles.paymentText}>
													<p className={styles.paymentTitle}>{item.title}</p>
													<p className={styles.paymentSubtitle}>{item.subtitle}</p>
												</div>
											</button>
										</li>
									))}
								</ul>
							) : null}

							{hasMore && !showAllPayments ? (
								<button type="button" className={styles.showAll} onClick={() => setShowAllPayments(true)}>
									Показать все
								</button>
							) : null}
						</section>
					) : null}

					{linksUnlocked ? (
						<section className={styles.navCards} aria-label="Разделы профиля">
							{NAV_CARDS.map((card) => (
								<button
									key={card.key}
									type="button"
									className={styles.navCard}
									onClick={() => {
										if (card.key === 'documents') {
											navigate(ROUTES.profileDocuments);
										}

										if (card.key === 'statuses') {
											navigate(ROUTES.profileStatuses);
										}

										if (card.key === 'auto-reply') {
											navigate(ROUTES.profileAutoReply);
										}
									}}
								>
									<span>{card.label}</span>
									<span className={styles.navCardIcon}>{card.icon}</span>
								</button>
							))}
						</section>
					) : null}

					<ul className={styles.linkList}>
						{linkItems.map((item) => (
							<li key={item.key} className={styles.linkItem}>
								<button
									type="button"
									className={styles.linkButton}
									onClick={() => {
										if (item.key === 'receipt') {
											navigate(ROUTES.profileGenerateReceipt);
										}

										if (item.key === 'certificate') {
											navigate(ROUTES.profileGenerateCertificate);
										}

										if (item.key === 'terms') {
											navigate(ROUTES.profileTerms);
										}
									}}
								>
									<span>{item.label}</span>
									<ArrowRightIcon className={styles.linkArrow} width={28} height={28} />
								</button>
							</li>
						))}
					</ul>
				</div>
			</div>

			<AddPaymentMethodDrawer
				open={isAddPaymentOpen}
				onClose={() => setIsAddPaymentOpen(false)}
				onSelect={handleSelectPaymentMethod}
			/>

			<PaymentWayDetailsDrawer selection={selectedPayment} onClose={() => setSelectedPayment(null)} />
		</div>
	);
}
